import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { isNetworkError } from '@xver/libs/connectionNotifier';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BackupWithResponse } from '../../../../../@types/backup';
import { Endpoints } from '../../../../../apis';

export function useBackupsInfiniteQuery(instanceId: number) {
    const { t } = useTranslation('cloudComputeInstance');
    const hasShownError = useRef(false);

    const query = useInfiniteQuery({
        queryKey: ['api.v1.backups.list', instanceId],
        queryFn: ({ pageParam }): Promise<BackupWithResponse> =>
            Endpoints.v1.Backups.getAllBackup(instanceId, pageParam),
        initialPageParam: 1,
        getNextPageParam: (last: BackupWithResponse) =>
            last.page < last.total_pages ? last.page + 1 : undefined,
        enabled: instanceId > 0,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchInterval: 10_000,
    });

    const { error } = query;

    useEffect(() => {
        if (error && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('backup.queryFailedTitle'),
                message: t('backup.listQueryFailedMessage'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (!error) {
            hasShownError.current = false;
        }
    }, [error, t]);

    return query;
}
